// Turn a raw platform error into a plain-English diagnosis + a suggested next
// step, so a failure is actionable instead of cryptic. Shared by the owner
// Command Center (system-wide failures) and the per-user Activity log. Order
// matters — most specific patterns first, with a catch-all last. Grows as new
// error shapes show up in the wild.

export interface Diagnosis {
  diagnosis: string;
  action: string;
}

/**
 * Whether a failed leg is worth retrying AS-IS (a transient/temporary problem)
 * vs. one that needs a change first (reconnect, buy credits, shorten, pick a
 * Page…). Drives the failure action: a plain "Retry" for the transient ones, and
 * "Re-post" (reopen the composer) for the ones a straight retry can't fix.
 */
export function isRetryable(reason: string): boolean {
  const r = reason.toLowerCase();
  // Needs a specific fix first — retrying the same thing just fails the same way.
  if (/unaudited_client|hasn.t approved this app|content posting audit/.test(r)) return false;
  if (/url_ownership_unverified|domain.*(verif|ownership)/.test(r)) return false;
  if (
    /reconnect|re-?auth|expired|revoked|invalid_grant|no_refresh_token|invalid.*token|token.*(expired|invalid)/.test(
      r,
    )
  )
    return false;
  if (/credit|insufficient/.test(r)) return false;
  if (/select a (facebook )?page|no page|page.*(chosen|selected)/.test(r)) return false;
  if (/not connected|no .*account|isn.t connected/.test(r)) return false;
  if (/too long|duration|exceeds|max.*length/.test(r)) return false;
  // Everything else — rate limits, "still processing", a momentary platform
  // hiccup, or an unknown error — is worth a straight retry.
  return true;
}

export function diagnose(reason: string): Diagnosis {
  const r = reason.toLowerCase();
  if (/unaudited_client|hasn.t approved this app|content posting audit/.test(r)) {
    return {
      diagnosis:
        "TikTok rejected this as a public post (unaudited-client restriction). The app's Content Posting audit is approved, so this usually points to a stale TikTok connection or an account still restricted to private posting.",
      action:
        "Reconnect the TikTok account on the Connections page and try again. If it keeps happening, contact support so we can check the app's TikTok status. Your other platforms are unaffected.",
    };
  }
  if (/url_ownership_unverified|domain.*(verif|ownership)/.test(r)) {
    return {
      diagnosis: "TikTok couldn't fetch the video because the media domain isn't verified.",
      action: "This is a platform-config issue — send it to support and we'll verify the domain.",
    };
  }
  if (/reconnect|re-?auth|expired|revoked|invalid.*token|token.*(expired|invalid)/.test(r)) {
    return {
      diagnosis: "This account's connection expired or was disconnected, so it couldn't publish.",
      action: "Reconnect that account on the Connections page, then re-post.",
    };
  }
  if (/rate|too many|spam_risk|quota|limit reached|daily.*cap|\b429\b/.test(r)) {
    return {
      diagnosis: "The platform is rate-limiting this account, or a daily limit was reached.",
      action: "Wait a while and try again — this usually clears once the limit window resets.",
    };
  }
  if (/credit|insufficient/.test(r)) {
    return {
      diagnosis: "You ran out of publish credits.",
      action: "Top up or upgrade under Settings → Billing, then re-post.",
    };
  }
  if (/select a (facebook )?page|no page|page.*(chosen|selected)/.test(r)) {
    return {
      diagnosis: "Facebook is connected but no Page was chosen to publish to.",
      action: "Pick a Facebook Page in Connections, then re-post.",
    };
  }
  if (/not connected|no .*account|isn.t connected/.test(r)) {
    return {
      diagnosis: "That platform wasn't connected when the post went out.",
      action: "Connect it on the Connections page, then re-post.",
    };
  }
  if (/too long|duration|exceeds|max.*length/.test(r)) {
    return {
      diagnosis: "The video is longer than the platform allows.",
      action: "Trim the clip under the platform's limit and re-post.",
    };
  }
  return {
    diagnosis: "An unexpected platform error — the raw message is shown above.",
    action: "If it keeps happening, copy the message and send it to support.",
  };
}
